import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../api';
import { formatCurrency, formatDate, initCurrencySettings } from '../utils/format';
import { getCountryConfig } from '../utils/countries';
import StatusBadge from '../components/StatusBadge';

export default function PublicInvoiceView() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    publicApi.getInvoice(invoiceId)
      .then((invRes) => {
        const config = getCountryConfig(invRes.data.business?.country || 'IN');
        initCurrencySettings(config.locale, config.currency, config.symbol);
        setInvoice(invRes.data);
        setBusiness(invRes.data.business);
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [invoiceId]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await publicApi.downloadPdf(invoiceId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-8 h-8 border-4 border-primary-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (notFound || !invoice) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center text-gray-500">
        <p className="text-lg font-medium mb-1">Invoice not found</p>
        <p className="text-sm">This invoice link may be invalid or has been removed.</p>
      </div>
    </div>
  );

  const taxLabel = getCountryConfig(business?.country).invoiceTaxLabel;
  const totalsTaxLabel = getCountryConfig(business?.country).totalsTaxLabel;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-end mb-4">
          <button
            onClick={downloadPDF}
            disabled={downloading}
            className="flex items-center justify-center gap-2 bg-gray-700 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {downloading ? 'Preparing...' : 'Download PDF'}
          </button>
        </div>

        <div className="card">
          {/* Header */}
          <div className="flex flex-row gap-3 mb-8">
            {/* Left: Logo + Business info */}
            <div className="flex-1 min-w-0">
              {business?.logo_url && (
                <img src={business.logo_url} alt="" className="w-14 h-14 object-contain rounded mb-2" />
              )}
              <h2 className="text-base md:text-2xl font-bold text-primary-700 break-words">{business?.name}</h2>
              {business?.address && <p className="text-xs text-gray-500 mt-1 whitespace-pre-line break-words">{business.address}</p>}
              {business?.gst_number && <p className="text-xs text-gray-400 mt-1">{taxLabel}: {business.gst_number}</p>}
              {business?.email && <p className="text-xs text-gray-400 break-all">{business.email}</p>}
              {business?.phone && <p className="text-xs text-gray-400">{business.phone}</p>}
            </div>
            {/* Right: INVOICE label + number + status */}
            <div className="flex-shrink-0 text-right">
              <div className="text-sm md:text-3xl font-bold text-gray-200">INVOICE</div>
              <p className="font-bold text-gray-900 text-sm md:text-lg">{invoice.invoice_number}</p>
              <StatusBadge status={invoice.status} />
            </div>
          </div>

          {/* Bill To */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-8">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Bill To</p>
              <p className="font-semibold text-gray-900">{invoice.client_name}</p>
              {invoice.client_company && <p className="text-sm text-gray-600">{invoice.client_company}</p>}
              {invoice.client_address && <p className="text-sm text-gray-500 whitespace-pre-line">{invoice.client_address}</p>}
              {invoice.client_email && <p className="text-sm text-gray-500">{invoice.client_email}</p>}
              {invoice.client_gst_number && <p className="text-xs text-gray-400">{taxLabel}: {invoice.client_gst_number}</p>}
            </div>
            <div className="text-left md:text-right">
              <div className="space-y-1 text-sm">
                <div>
                  <span className="text-gray-400">Invoice Date: </span>
                  <span className="text-gray-700 font-medium">{formatDate(invoice.invoice_date)}</span>
                </div>
                <div>
                  <span className="text-gray-400">Due Date: </span>
                  <span className="text-gray-700 font-medium">{formatDate(invoice.due_date)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm min-w-[480px]">
            <thead>
              <tr className="bg-primary-700 text-white">
                <th className="text-left px-4 py-2 rounded-tl-lg font-medium">#</th>
                <th className="text-left px-4 py-2 font-medium">Description</th>
                <th className="text-right px-4 py-2 font-medium">Qty</th>
                <th className="text-right px-4 py-2 font-medium">Rate</th>
                <th className="text-right px-4 py-2 rounded-tr-lg font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.line_items.map((item, idx) => (
                <tr key={item.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="px-4 py-2.5 text-gray-400">{idx + 1}</td>
                  <td className="px-4 py-2.5 text-gray-700">{item.description}</td>
                  <td className="px-4 py-2.5 text-right text-gray-700">{item.quantity}</td>
                  <td className="px-4 py-2.5 text-right text-gray-700">{formatCurrency(item.rate)}</td>
                  <td className="px-4 py-2.5 text-right font-medium text-gray-900">{formatCurrency(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-6">
            <div className="w-64 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>{formatCurrency(invoice.subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>{totalsTaxLabel} ({invoice.gst_percentage}%)</span>
                <span>{formatCurrency(invoice.gst_amount)}</span>
              </div>
              <div className="flex justify-between font-bold text-gray-900 text-base border-t border-gray-200 pt-2">
                <span>Total</span>
                <span>{formatCurrency(invoice.total_amount)}</span>
              </div>
              {invoice.amount_paid > 0 && (
                <>
                  <div className="flex justify-between text-green-600">
                    <span>Paid</span>
                    <span>- {formatCurrency(invoice.amount_paid)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-primary-700 border-t border-gray-200 pt-2">
                    <span>Balance Due</span>
                    <span>{formatCurrency(invoice.balance_due)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Notes</p>
              <p className="text-sm text-gray-600 whitespace-pre-line">{invoice.notes}</p>
            </div>
          )}

          {/* Payment Details */}
          {business?.bank_details && (
            <div className="border-t border-gray-100 pt-4 mt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Payment Details</p>
              <div className="text-sm">
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium mb-0.5">Bank Details</p>
                  <p className="text-blue-800 whitespace-pre-line text-xs">{business.bank_details}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment History */}
          {invoice.payments && invoice.payments.length > 0 && (
            <div className="border-t border-gray-100 pt-4 mt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Payment History</p>
              <div className="space-y-2">
                {invoice.payments.map(p => (
                  <div key={p.id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50">
                    <span className="text-gray-600">{p.payment_mode} · {formatDate(p.payment_date)}</span>
                    <span className="font-medium text-gray-800">{formatCurrency(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">Powered by BillTrack</p>
      </div>
    </div>
  );
}
